/**
 * Super Admin — Analytics (iter 76)
 *
 * Surfaces:
 *   - Our own event counters (mirrored from Clarity custom-event firings)
 *   - Top pages, device split, daily volume
 *   - Deep-link buttons to Microsoft Clarity portal for heatmaps + session replays
 */
import { useEffect, useState } from 'react';
import {
  Activity, Users, MousePointerClick, Smartphone, Monitor, Tablet,
  TrendingUp, ExternalLink, Eye, RefreshCw, Loader2, Terminal, Sparkles,
  AlertTriangle, Languages,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CLARITY_PROJECT_ID = (process.env.REACT_APP_CLARITY_PROJECT_ID || '').trim();

// Friendly labels for our custom event names.
const EVENT_LABELS = {
  hero_cta_click:           'Hero CTA',
  try_free_click:           'Try Free',
  pricing_click:            'See Pricing',
  book_demo_click:          'Book Demo',
  whatsapp_click:           'WhatsApp Contact',
  signup_success:           'Sign-ups',
  login_success:            'Logins',
  lesson_plan_generated:    'Lesson Plans',
  worksheet_generated:      'Worksheets',
  question_paper_generated: 'Question Papers',
  assignment_generated:     'Assignments',
  test_generated:           'Tests',
  exam_generated:           'Exams',
  summary_generated:        'Summaries',
  ppt_generated:            'Slide Decks',
  section_a_generated:      'Section A — other',
  section_b_generated:      'Section B — other',
  newsletter_subscribed:    'Newsletter signups',
  contact_form_submitted:   'Contact forms',
  payment_success:          'Payments',
  blog_post_viewed:         'Blog post views',
  blog_post_bookmarked:     'Blog bookmarks',
  blog_post_rated:          'Blog ratings',
  demo_requested:           'Demo requests',
  page_view:                'Page views',
};


async function fetchOverview(days) {
  const token = localStorage.getItem('sip_token');
  const res = await fetch(`${API_URL}/api/site-analytics/overview?days=${days}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Analytics fetch failed: ${res.status}`);
  return res.json();
}


function StatCard({ icon: Icon, label, value, accent = 'indigo', testid }) {
  const accents = {
    indigo:  'from-indigo-500/10 to-indigo-500/0 text-indigo-700 dark:text-indigo-300 border-indigo-200/60 dark:border-indigo-900/40',
    emerald: 'from-emerald-500/10 to-emerald-500/0 text-emerald-700 dark:text-emerald-300 border-emerald-200/60 dark:border-emerald-900/40',
    amber:   'from-amber-500/10 to-amber-500/0 text-amber-700 dark:text-amber-300 border-amber-200/60 dark:border-amber-900/40',
    slate:   'from-slate-500/10 to-slate-500/0 text-slate-700 dark:text-slate-300 border-slate-200/60 dark:border-slate-800',
  };
  return (
    <div
      className={`rounded-xl border bg-gradient-to-br ${accents[accent]} p-4`}
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-white/70 dark:bg-slate-900/40 flex items-center justify-center">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
          <div className="text-2xl font-bold mt-0.5">{value}</div>
        </div>
      </div>
    </div>
  );
}


export default function SuperAdminAnalyticsPage() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await fetchOverview(days);
      setData(d);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  // Iter 77c — BOSCO usage block (Phase 2 minimal slice)
  const [bosco, setBosco] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('sip_token');
    if (!token) return;
    fetch(`${API_URL}/api/bosco/admin/analytics?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setBosco)
      .catch(() => {});
  }, [days]);

  const devices = data?.devices || {};
  const desktop = devices.desktop || 0;
  const mobile  = devices.mobile  || 0;
  const tablet  = devices.tablet  || 0;
  const bot     = devices.bot     || 0;
  const total   = desktop + mobile + tablet;
  const pct = (n) => (total ? Math.round((100 * n) / total) : 0);

  const clarityPortalUrl = CLARITY_PROJECT_ID
    ? `https://clarity.microsoft.com/projects/view/${CLARITY_PROJECT_ID}/dashboard`
    : null;
  const heatmapsUrl = CLARITY_PROJECT_ID
    ? `https://clarity.microsoft.com/projects/view/${CLARITY_PROJECT_ID}/heatmaps`
    : null;
  const recordingsUrl = CLARITY_PROJECT_ID
    ? `https://clarity.microsoft.com/projects/view/${CLARITY_PROJECT_ID}/recordings`
    : null;

  return (
    <div className="space-y-6" data-testid="super-admin-analytics-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="h-6 w-6 text-indigo-600" />
            Analytics
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Visitor behaviour, top events, and a direct link into Microsoft Clarity for heatmaps & session replays.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger className="w-32" data-testid="analytics-range-picker">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={load} data-testid="analytics-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={Users}
          label="Total visitors"
          value={loading ? '…' : (data?.totalVisitors ?? 0).toLocaleString()}
          accent="indigo"
          testid="analytics-stat-visitors"
        />
        <StatCard
          icon={MousePointerClick}
          label="Total events"
          value={loading ? '…' : (data?.totalEvents ?? 0).toLocaleString()}
          accent="emerald"
          testid="analytics-stat-events"
        />
        <StatCard
          icon={Smartphone}
          label="Mobile share"
          value={loading ? '…' : `${pct(mobile)}%`}
          accent="amber"
          testid="analytics-stat-mobile-share"
        />
        <StatCard
          icon={TrendingUp}
          label="Range"
          value={`${days} days`}
          accent="slate"
          testid="analytics-stat-range"
        />
      </div>

      {/* Clarity deep-link cards */}
      <Card className="p-4">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
          Microsoft Clarity portal
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <a
            href={clarityPortalUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className={`rounded-lg border p-4 flex items-center justify-between gap-3 transition-shadow hover:shadow-md ${
              clarityPortalUrl ? 'border-indigo-200/60 dark:border-indigo-900/40 bg-indigo-50/40 dark:bg-indigo-950/20' : 'opacity-50 pointer-events-none'
            }`}
            data-testid="analytics-link-dashboard"
          >
            <div>
              <div className="font-semibold">Dashboard</div>
              <div className="text-xs text-slate-500">Full Clarity overview</div>
            </div>
            <ExternalLink className="h-4 w-4 text-indigo-600" />
          </a>
          <a
            href={heatmapsUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className={`rounded-lg border p-4 flex items-center justify-between gap-3 transition-shadow hover:shadow-md ${
              heatmapsUrl ? 'border-emerald-200/60 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-950/20' : 'opacity-50 pointer-events-none'
            }`}
            data-testid="analytics-link-heatmaps"
          >
            <div>
              <div className="font-semibold">Heatmaps</div>
              <div className="text-xs text-slate-500">Click + scroll heatmaps per page</div>
            </div>
            <ExternalLink className="h-4 w-4 text-emerald-600" />
          </a>
          <a
            href={recordingsUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className={`rounded-lg border p-4 flex items-center justify-between gap-3 transition-shadow hover:shadow-md ${
              recordingsUrl ? 'border-amber-200/60 dark:border-amber-900/40 bg-amber-50/40 dark:bg-amber-950/20' : 'opacity-50 pointer-events-none'
            }`}
            data-testid="analytics-link-recordings"
          >
            <div>
              <div className="font-semibold">Session recordings</div>
              <div className="text-xs text-slate-500">Watch real user sessions</div>
            </div>
            <ExternalLink className="h-4 w-4 text-amber-600" />
          </a>
        </div>
        {!CLARITY_PROJECT_ID && (
          <p className="mt-3 text-xs text-amber-700 dark:text-amber-400">
            <strong>Clarity Project ID not configured.</strong> Set <code>REACT_APP_CLARITY_PROJECT_ID</code> in <code>frontend/.env</code> and redeploy.
          </p>
        )}
      </Card>

      {/* Top events + Top pages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
            Most clicked / most fired events
          </div>
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
          ) : !data?.topEvents?.length ? (
            <p className="text-sm text-slate-500 italic py-6">No events recorded yet in this range.</p>
          ) : (
            <table className="w-full text-sm" data-testid="analytics-top-events">
              <thead className="text-left text-slate-500 border-b">
                <tr>
                  <th className="py-2 pr-3">Event</th>
                  <th className="py-2 pr-3 text-right">Count</th>
                </tr>
              </thead>
              <tbody>
                {data.topEvents.map((e) => (
                  <tr key={e.event} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 pr-3 font-medium">
                      {EVENT_LABELS[e.event] || e.event}
                      <code className="ml-2 text-[10px] text-slate-400">{e.event}</code>
                    </td>
                    <td className="py-2 pr-3 text-right font-semibold">{e.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
            Top pages
          </div>
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
          ) : !data?.topPages?.length ? (
            <p className="text-sm text-slate-500 italic py-6">No page-tagged events yet.</p>
          ) : (
            <table className="w-full text-sm" data-testid="analytics-top-pages">
              <thead className="text-left text-slate-500 border-b">
                <tr>
                  <th className="py-2 pr-3">Path</th>
                  <th className="py-2 pr-3 text-right">Events</th>
                </tr>
              </thead>
              <tbody>
                {data.topPages.map((p) => (
                  <tr key={p.path} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 pr-3 font-mono text-xs">{p.path}</td>
                    <td className="py-2 pr-3 text-right font-semibold">{p.count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* BOSCO Analytics™ (Phase 2) */}
      <BoscoAnalyticsBlock data={bosco} days={days} />

      {/* Device split + Daily volume */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
            Device distribution
          </div>
          <div className="space-y-2" data-testid="analytics-device-split">
            <DeviceBar icon={Monitor} label="Desktop" n={desktop} pct={pct(desktop)} color="indigo" />
            <DeviceBar icon={Smartphone} label="Mobile" n={mobile} pct={pct(mobile)} color="emerald" />
            <DeviceBar icon={Tablet} label="Tablet" n={tablet} pct={pct(tablet)} color="amber" />
            {bot > 0 && (
              <DeviceBar icon={Eye} label="Bots (excluded from %)" n={bot} pct={0} color="slate" muted />
            )}
          </div>
        </Card>

        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">
            Daily events
          </div>
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
          ) : !data?.daily?.length ? (
            <p className="text-sm text-slate-500 italic py-6">No events recorded yet in this range.</p>
          ) : (
            <DailyChart points={data.daily} />
          )}
        </Card>
      </div>
    </div>
  );
}


function DeviceBar({ icon: Icon, label, n, pct: percent, color, muted }) {
  const colors = {
    indigo:  'bg-indigo-500',
    emerald: 'bg-emerald-500',
    amber:   'bg-amber-500',
    slate:   'bg-slate-400',
  };
  return (
    <div className={muted ? 'opacity-60' : ''}>
      <div className="flex items-center justify-between mb-1 text-sm">
        <span className="flex items-center gap-2"><Icon className="h-4 w-4" /> {label}</span>
        <span className="font-semibold">{n.toLocaleString()}{!muted && ` · ${percent}%`}</span>
      </div>
      <div className="h-2 bg-slate-100 dark:bg-slate-800 rounded">
        <div className={`h-2 rounded ${colors[color]}`} style={{ width: `${Math.min(100, percent)}%` }} />
      </div>
    </div>
  );
}

function DailyChart({ points }) {
  const max = Math.max(1, ...points.map((p) => p.count));
  return (
    <div className="flex items-end gap-1 h-32 px-1" data-testid="analytics-daily-chart">
      {points.map((p) => (
        <div key={p.date} className="flex-1 flex flex-col items-center gap-1 group">
          <div
            className="w-full bg-indigo-500/70 hover:bg-indigo-500 rounded-t transition-colors"
            style={{ height: `${Math.max(2, (100 * p.count) / max)}%` }}
            title={`${p.date}: ${p.count} events`}
          />
          <div className="text-[9px] text-slate-400 opacity-0 group-hover:opacity-100">{p.date.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// BOSCO Analytics™ — Phase 2 surfacing block
// ─────────────────────────────────────────────────────────────
function BoscoAnalyticsBlock({ data, days }) {
  const loading = data === null || data === undefined;
  const empty = !loading && !data?.totalExecutions;

  return (
    <Card
      className="p-4 border-emerald-200/60 dark:border-emerald-900/40 bg-gradient-to-br from-emerald-50/30 to-black/0 dark:from-emerald-950/20"
      data-testid="bosco-analytics-block"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-lg bg-black text-emerald-400 flex items-center justify-center font-mono">
            <Terminal className="h-5 w-5" />
          </div>
          <div>
            <div className="text-base font-bold flex items-center gap-2">
              BOSCO Analytics™
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-emerald-300 text-emerald-700 dark:text-emerald-300">
                Elite Studio
              </Badge>
            </div>
            <div className="text-xs text-slate-500">Last {days} days · execution telemetry from <code>bosco_executions</code></div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="py-8 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
      )}

      {!loading && empty && (
        <p className="text-sm text-slate-500 italic py-6" data-testid="bosco-analytics-empty">
          No BOSCO commands executed in this range yet. Once Elite Individual users start running commands, telemetry will surface here.
        </p>
      )}

      {!loading && !empty && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard
              icon={Terminal}
              label="Total executions"
              value={(data.totalExecutions ?? 0).toLocaleString()}
              accent="emerald"
              testid="bosco-stat-total"
            />
            <StatCard
              icon={Users}
              label="Active users"
              value={(data.activeUsers ?? 0).toLocaleString()}
              accent="indigo"
              testid="bosco-stat-users"
            />
            <StatCard
              icon={AlertTriangle}
              label="Failures"
              value={(data.failures ?? 0).toLocaleString()}
              accent="amber"
              testid="bosco-stat-failures"
            />
            <StatCard
              icon={Sparkles}
              label="Success rate"
              value={data.successRate != null ? `${data.successRate}%` : '—'}
              accent="slate"
              testid="bosco-stat-success-rate"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Top commands */}
            <div className="rounded-lg border bg-white/60 dark:bg-slate-900/40 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                <Terminal className="h-3.5 w-3.5" /> Most used commands
              </div>
              {!data.topCommands?.length ? (
                <p className="text-sm text-slate-500 italic py-3">No command usage yet.</p>
              ) : (
                <table className="w-full text-sm" data-testid="bosco-top-commands">
                  <thead className="text-left text-slate-500 border-b">
                    <tr>
                      <th className="py-1.5 pr-3">Command</th>
                      <th className="py-1.5 pr-3 text-right">Runs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topCommands.map((c) => (
                      <tr key={c.key} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-1.5 pr-3 font-mono text-emerald-700 dark:text-emerald-300">{c.key}</td>
                        <td className="py-1.5 pr-3 text-right font-semibold">{c.count.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Top languages */}
            <div className="rounded-lg border bg-white/60 dark:bg-slate-900/40 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                <Languages className="h-3.5 w-3.5" /> Language split
              </div>
              {!data.topLanguages?.length ? (
                <p className="text-sm text-slate-500 italic py-3">No language data yet.</p>
              ) : (
                <div className="space-y-1.5" data-testid="bosco-top-langs">
                  {(() => {
                    const totalLang = data.topLanguages.reduce((s, l) => s + l.count, 0) || 1;
                    return data.topLanguages.map((l) => {
                      const pct = Math.round((100 * l.count) / totalLang);
                      return (
                        <div key={l.key || 'unknown'}>
                          <div className="flex items-center justify-between text-sm">
                            <span className="capitalize">{l.key || 'unknown'}</span>
                            <span className="font-semibold text-xs">{l.count} · {pct}%</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded">
                            <div className="h-1.5 bg-emerald-500 rounded" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>

          {/* Custom commands + Daily mini chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <div className="rounded-lg border bg-white/60 dark:bg-slate-900/40 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5" /> Custom commands
              </div>
              <div className="text-sm mb-3">
                <span className="font-semibold">{data.customCommandsTotal ?? 0}</span>
                <span className="text-slate-500"> registered custom commands across all Elite users</span>
              </div>
              {!data.customCommandUsage?.length ? (
                <p className="text-xs text-slate-500 italic">No custom-command executions in this range.</p>
              ) : (
                <table className="w-full text-sm" data-testid="bosco-custom-usage">
                  <tbody>
                    {data.customCommandUsage.slice(0, 8).map((c) => (
                      <tr key={c.key} className="border-b border-slate-100 dark:border-slate-800">
                        <td className="py-1 pr-3 font-mono text-amber-700 dark:text-amber-300 text-xs">{c.key}</td>
                        <td className="py-1 pr-3 text-right font-semibold text-xs">{c.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="rounded-lg border bg-white/60 dark:bg-slate-900/40 p-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5" /> Daily executions
              </div>
              {!data.daily?.length ? (
                <p className="text-xs text-slate-500 italic">No executions in this range.</p>
              ) : (
                <DailyChart points={data.daily} />
              )}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
